/**
 * Created by katarinababic on 18.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { ScheduleEntryRenderer } from '../ScheduleCalendar/ScheduleEntryRenderer';
import { useTrans } from '../../Store/System/LocalizationStore';
import { ActivitySchedule, ActivityScheduleApi } from '../../Model/Coach/Schedule/ActivitySchedule';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { Theme } from '../../Theme/Theme';
import { Translation } from '../../Components/Translation';

const SectionHeader = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  margin-bottom: 8px;
  color: ${Theme.Colors.textColor};

  @media (max-width: 992px) {
    margin-bottom: 24px;
  }
`;

const NoHintText = styled.p`
  font-size: 0.9rem;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 24px 0;
  line-height: 1.5;

  @media (max-width: 992px) {
    display: none;
  }
`;

const ActivitiesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoEntriesMessage = styled.div`
  color: ${Theme.Colors.warmGrey};
  font-size: 14px;
  line-height: 1.6;
  padding: 32px 24px;
  background: white;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`;

const ShowMoreButton = styled.button`
  background: transparent;
  border: 2px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  cursor: pointer;
  color: ${Theme.Colors.charcoal};
  font-weight: 600;
  font-size: 14px;
  padding: 12px 24px;
  margin-top: 12px;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: ${Theme.Colors.primary};
    border-color: ${Theme.Colors.primary};
    color: white;
  }
`;

export type HomeScreenScheduledActivitiesProps = {};

export const HomeScreenScheduledActivities: React.FC<HomeScreenScheduledActivitiesProps> = observer((props) => {
  const { t } = useTrans();

  const [schedule, setSchedule] = useState<ActivitySchedule | undefined>();
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    // fetch today's schedule and re-fetch when schedule changes (e.g., on deletion)
    const fetchSchedule = () => {
      const today = dayjs().format('YYYY-MM-DD');
      ActivityScheduleApi.find({ startDateTime: today, endDateTime: today })
        .then((res) => setSchedule(res))
        .catch(() => {
          console.log('Failed to fetch schedule');
        });
    };
    fetchSchedule();
    window.addEventListener('scheduleChanged', fetchSchedule);
    return () => {
      window.removeEventListener('scheduleChanged', fetchSchedule);
    };
  }, []);

  const listData = Object.values(schedule?.days || {})
    .flatMap((day) => day.entries)
    .sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
  const toShow = showAll ? listData : listData.slice(0, 3);

  return (
    <span>
      <SectionHeader>
        <Translation
          i18nKey="home.yourScheduledActivities"
          defaultValue={`<span style="color: #00dda2">Today's</span> Schedule`}
        />
        </SectionHeader>
      <NoHintText>&nbsp;</NoHintText>
      {toShow.length === 0 ? (
        <NoEntriesMessage>
          {t('home.noScheduledEntries', {
            defaultValue:
              'You don\'t have any scheduled activities yet. Check out today\'s suggestions or explore more options.',
          })}
        </NoEntriesMessage>
      ) : (
        <ActivitiesGrid>
          {toShow.map((entry) => (
            <ScheduleEntryRenderer key={entry.id} entry={entry} date={dayjs().format('YYYY-MM-DD')} />
          ))}
        </ActivitiesGrid>
      )}
      {listData.length > 3 && (
        <ShowMoreButton onClick={() => setShowAll(!showAll)}>
          {showAll
            ? t('home.showLess', { defaultValue: 'Show less' })
            : t('home.showMore', { defaultValue: 'Show more' })}
        </ShowMoreButton>
      )}
    </span>
  );
});
